import {
  DataGrid,
  FilterToolbar,
  StatusBadge,
  useNotifications,
  type FilterDef,
  type GridQuery
} from '@ijoin/components';
import {
  FidelityCarrierContractsCarrier,
  GetAdminApiKeysParams,
  getGetAdminApiKeysQueryKey,
  useGetAdminApiKeyEntityTypes,
  useGetAdminApiKeys,
  useGetAnnuityCarriers,
  usePostAdminApiKeyRevoke
} from '@ijoin/hooks';
import { Chip, Stack } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { keepPreviousData, useQueryClient } from '@tanstack/react-query';

import { startCase } from 'lodash';
import { FC, useState } from 'react';
import { useToggle } from 'usehooks-ts';

import { ApiKeyActionsCell } from './ApiKeyActionsCell';
import { CreateApiKeyDialog } from './CreateApiKeyDialog';

export const ApiKeysSection: FC<{ annuityIntegrationId?: string }> = props => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();

  const [query, setQuery] = useState<GridQuery>();
  const [createDialogOpen, toggleCreateDialogOpen] = useToggle(false);

  const scopedQuery: GetAdminApiKeysParams | undefined = query && {
    ...(query as GetAdminApiKeysParams),
    ...(props.annuityIntegrationId && {
      annuityIntegrationId: props.annuityIntegrationId
    })
  };

  const apiKeys = useGetAdminApiKeys(scopedQuery, {
    query: { enabled: !!query, placeholderData: keepPreviousData }
  });

  const entityTypes = useGetAdminApiKeyEntityTypes();
  const carriers = useGetAnnuityCarriers();

  const revokeApiKey = usePostAdminApiKeyRevoke({
    mutation: {
      onError: () => notifications.error('Failed to revoke API key'),
      onSuccess: () => {
        notifications.success('API key revoked');
        queryClient.invalidateQueries({
          queryKey: getGetAdminApiKeysQueryKey()
        });
      }
    }
  });

  const filterDefs: FilterDef[] = [
    { label: 'Name', name: 'name', type: 'text' },
    {
      isLoading: entityTypes.isFetching,
      label: 'Entity Type',
      name: 'entityType',
      options: (entityTypes.data?.entityTypes ?? []).map(value => ({
        label: startCase(value),
        value
      })),
      type: 'select'
    },
    ...(props.annuityIntegrationId
      ? []
      : [
          {
            isLoading: carriers.isFetching,
            label: 'Carrier',
            name: 'annuityIntegrationId',
            options: (carriers.data ?? []).map(carrier => ({
              label: carrier.description,
              value: carrier.annuityIntegrationId
            })),
            type: 'select'
          } as FilterDef,
          {
            label: 'Fidelity Carrier',
            name: 'fidelityCarrier',
            options: Object.values(FidelityCarrierContractsCarrier).map(
              value => ({ label: value, value })
            ),
            type: 'select'
          } as FilterDef
        ]),
    {
      label: 'Status',
      name: 'status',
      options: [
        { label: 'Active', value: 'active' },
        { label: 'Expired', value: 'expired' },
        { label: 'Revoked', value: 'revoked' }
      ],
      type: 'select'
    }
  ];

  const columns: GridColDef[] = [
    { field: 'name', flex: 1, headerName: 'Name' },
    {
      field: 'entityType',
      flex: 0.8,
      headerName: 'Entity Type',
      renderCell: params => startCase(params.value)
    },
    { field: 'entityName', flex: 1, headerName: 'Entity', sortable: false },
    {
      field: 'fidelityCarrier',
      flex: 0.7,
      headerName: 'Fidelity Carrier',
      renderCell: params => params.value ?? '-',
      sortable: false
    },
    {
      field: 'scopes',
      flex: 1.5,
      headerName: 'Scopes',
      renderCell: params =>
        params.value?.length ? (
          <Stack direction='row' flexWrap='wrap' gap={0.5}>
            {params.value.map((scope: string) => (
              <Chip key={scope} label={scope} size='small' />
            ))}
          </Stack>
        ) : (
          '-'
        ),
      sortable: false
    },
    {
      field: 'keyPrefix',
      flex: 0.6,
      headerName: 'Prefix',
      sortable: false,
      valueFormatter: value => `${value}…`
    },
    {
      field: 'status',
      flex: 0.6,
      headerName: 'Status',
      renderCell: params => (
        <StatusBadge
          severity={
            params.value === 'active'
              ? 'success'
              : params.value === 'revoked'
                ? 'error'
                : 'warning'
          }>
          {params.value}
        </StatusBadge>
      )
    },
    {
      field: 'createdAt',
      flex: 0.8,
      headerName: 'Created',
      renderCell: params =>
        params.value ? new Date(params.value).toLocaleDateString() : ''
    },
    {
      field: 'expiresAt',
      flex: 0.8,
      headerName: 'Expires',
      renderCell: params =>
        params.value ? new Date(params.value).toLocaleDateString() : ''
    },
    {
      field: 'lastUsedAt',
      flex: 0.8,
      headerName: 'Last Used',
      renderCell: params =>
        params.value ? new Date(params.value).toLocaleDateString() : ''
    },
    {
      align: 'center',
      field: 'actions',
      headerName: '',
      renderCell: params =>
        params.row.status === 'active' ? (
          <ApiKeyActionsCell
            apiKeyId={params.row.apiKeyId}
            name={params.row.name}
            onRevoke={() =>
              revokeApiKey.mutate({ apiKeyId: params.row.apiKeyId })
            }
          />
        ) : null,
      sortable: false,
      width: 65
    }
  ];

  return (
    <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
      <DataGrid
        autoHeight={false}
        columns={columns}
        disableColumnMenu
        disableRowSelectionOnClick
        filterDefs={filterDefs}
        getRowHeight={() => 'auto'}
        getRowId={row => row.apiKeyId}
        initialSort={[{ field: 'createdAt', sort: 'desc' }]}
        loading={!query || apiKeys.isFetching}
        onClear={setQuery}
        onSearch={setQuery}
        pageSize={25}
        pageSizeOptions={[10, 25, 50, 100]}
        paginationMode='server'
        rowCount={apiKeys.data?.pagination?.total ?? 0}
        rows={apiKeys.data?.data ?? []}
        showColumnMenu
        showToolbar
        slotProps={{
          root: { 'data-component': 'dataGrid', 'data-testid': 'adminApiKeys' },
          toolbar: {
            addLabel: 'API Key',
            addTestId: 'createAdminApiKey',
            onAdd: toggleCreateDialogOpen,
            onRefresh: apiKeys.refetch
          }
        }}
        slots={{ toolbar: FilterToolbar }}
        sortingMode='server'
        sx={{
          '& .MuiDataGrid-cell': {
            alignItems: 'center',
            display: 'flex',
            py: 1
          },
          flex: 1,
          minHeight: 0
        }}
      />

      {createDialogOpen && (
        <CreateApiKeyDialog onClose={toggleCreateDialogOpen} />
      )}
    </Stack>
  );
};

ApiKeysSection.displayName = 'ApiKeysSection';
